import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import CounterInfoIcon from '../../../../../atoms/icons/CounterInfoIcon';
import useNav from '../../../../../hooks/useNav';
import useMixPanel from '../../../../../hooks/useMixPanel';
import { EVENT_NAMES, TRACK_PROFILE_VIEWS_FOR_OFFER_MADE } from '../../../../../constants/mixpanel';
import OneTapSwipeOfferMediaRenderDots from './OneTapSwipeOfferMediaRenderDots';
import { DotsAnimatedWrapper } from '../../../../common/CommonStyle';
import { animated, useTransition } from '@react-spring/web';
import { useOffers } from '../../../../../contexts/OffersContext';

const MediaCounterContainer = styled('div')({
  background: '#000',
  height: 41,
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
});

const CounterWrapper = styled('div')({
  display: 'inline-block',
  marginRight: 1,
  marginTop: 2,
  marginLeft: '80px',
  width: 'calc(100% - 160px)',
  textAlign: 'center',
});

const DotsWrapper = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '5px 8px',
  borderRadius: '8px',
  background: '#262626',
  gap: 6,
});

const InfoIconWrapper = styled('div')({
  padding: '1px 32px 11px 22px',
  cursor: 'pointer',
});

interface OneTapSwipeOfferMediaCounterProps {
  photos: any;
  currentSlide: number;
  hashId: string;
  otherAccountIsGenerous: boolean;
}

const OneTapSwipeOfferMediaCounter = ({
  photos,
  currentSlide,
  hashId,
  otherAccountIsGenerous,
}: OneTapSwipeOfferMediaCounterProps): ReactElement => {
  const { goTo } = useNav();
  const { setMixpanelDataByKeyHandler } = useMixPanel();
  const { animateDirection, isDotsAnimated, setIsDotsAnimated } = useOffers();

  const oneTapSwipeDotRef = useRef([]);
  const [ease, setEase] = useState({ x1: 0 });
  const [applyAnimation, setApplyAnimation] = useState(false);

  useEffect(() => {
    return () => {
      setIsDotsAnimated(false);
    };
  }, []);

  useLayoutEffect(() => {
    if (photos.length === 2) {
      const x1 = oneTapSwipeDotRef?.current[0]?.offsetLeft;

      setEase({ x1: x1 });
    } else {
      setApplyAnimation(false);
      const nextReft =
        animateDirection === 'left'
          ? oneTapSwipeDotRef?.current[currentSlide + 1]
          : oneTapSwipeDotRef?.current[currentSlide];
      const currentRef = oneTapSwipeDotRef?.current[currentSlide];
      const x1 = animateDirection === 'left' ? nextReft?.offsetLeft : currentRef?.offsetLeft;

      setEase({ x1 });
    }

    setApplyAnimation(!(currentSlide === 0 && !isDotsAnimated));
  }, [photos, currentSlide, oneTapSwipeDotRef?.current[currentSlide], animateDirection]);

  const transitions = useTransition(currentSlide, {
    keys: currentSlide,
    from: {
      opacity: 1,
      transform: 'scaleX(1)',
    },
    enter: {
      opacity: 0,
      transform: 'scaleX(0)',
    },
    delay: 300,
  });

  const renderAnimatedDiv = () => {
    let diffX = Math.abs(ease.x1 - 12);

    if (photos.length === 2) {
      diffX = Math.abs(ease.x1);
    }

    return (
      <DotsAnimatedWrapper style={{ display: 'flex' }}>
        {transitions(styles => (
          <animated.div
            style={{
              ...styles,
              position: 'absolute',
              height: 6,
              backgroundColor: 'white',
              borderRadius: '3px 3px 3px 3px',
              visibility: isDotsAnimated && applyAnimation ? 'visible' : 'hidden',
              top: '-11px',
              left: `${diffX + 'px'}`,
              width: '19px',
              transformOrigin: animateDirection === 'left' ? '0% 100%' : '100% 0%',
              transition: 'transform 0.3s ease-out forwards',
            }}
          />
        ))}
      </DotsAnimatedWrapper>
    );
  };

  return (
    <MediaCounterContainer>
      <CounterWrapper className="counter-info-wrapper">
        {photos.length > 0 && (
          <>
            <DotsWrapper className="dots-wrapper">
              {photos.map((item, index) => {
                return (
                  <OneTapSwipeOfferMediaRenderDots
                    ref={element => {
                      oneTapSwipeDotRef.current[index] = element;
                    }}
                    key={index}
                    item={item}
                    index={index}
                    currentSlide={currentSlide}
                    photos={photos}
                  />
                );
              })}
            </DotsWrapper>
            {renderAnimatedDiv()}
          </>
        )}
      </CounterWrapper>
      <InfoIconWrapper
        onClick={() => {
          setMixpanelDataByKeyHandler({
            key: EVENT_NAMES.OFFER_MADE,
            payload: {
              source: !otherAccountIsGenerous
                ? TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewReceivedRequest
                : TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewReceivedOffers,
            },
          });
          goTo(`/profile/${hashId}`);
        }}
        className="pressable"
      >
        <CounterInfoIcon />
      </InfoIconWrapper>
    </MediaCounterContainer>
  );
};

export default OneTapSwipeOfferMediaCounter;
